import { type TransactionSummaryDTO, useIntl } from '@capsicum/common';
import { ShortCode } from '@capsicum/socios-core';
import { useMemo } from 'react';

import { formatTransactionSum, groupTransactionsByDay } from '../util';

export type ListItem =
	| { dailySum: string; date: string; type: 'header' }
	| { transaction: TransactionSummaryDTO; type: 'transaction' };

export const useTransactionHistoryDayGrouping = ({
	data,
	excludeLastDate = false
}: {
	data: Array<TransactionSummaryDTO> | undefined;
	excludeLastDate?: boolean;
}) => {
	const { $t } = useIntl();

	const { dataForList, numberOfDates, transactionsByDay } = useMemo(() => {
		const transactionsByDay = groupTransactionsByDay(data) ?? {};
		const days = Object.entries(transactionsByDay);

		if (excludeLastDate) {
			days.pop();
		}

		const [firstDay] = days;
		const firstTransactionArray = firstDay?.[1];

		const currencyShortCode: string | undefined = firstTransactionArray?.[0]?.currencyShortCode;
		const chilizTransaction = currencyShortCode === ShortCode.CHZ;

		const dataForList: Array<ListItem> = days.flatMap(([date, transactions]) => {
			const dailyDifference = formatTransactionSum(transactions);

			let dailySum;

			if (chilizTransaction) {
				dailySum = `${dailyDifference} ${currencyShortCode}`;
			} else {
				dailySum = $t('wallet.transactionHistory.dailyFTSum', { dailySum: dailyDifference });
			}

			return [
				{ dailySum, date: date, type: 'header' as const },
				...transactions.map(tx => ({ transaction: tx, type: 'transaction' as const }))
			];
		});

		return { dataForList, numberOfDates: days.length, transactionsByDay };
	}, [data, excludeLastDate, $t]);

	return {
		dataForList,
		numberOfDates,
		transactionsByDay
	};
};
